import { makeAPICall } from '@gokwik/utilities'
import APIEndPoints from '@library/utilities/constants/apiEndpoints'
import { MerchantConfigKeys } from '@library/utilities/constants/constants'
import { useAppDispatch, useInterval } from '@library/utilities/hooks'
import { logEvent } from '@library/utilities/userLogsEvents/userLogEvents'
import { fetchUserDetails } from '@store/user'
import { getUserData, getMerchantDetails, getMerchantConfigs, getMerchantPlatform } from '@store/user/selectors'
import { Button, ArrowRightOutlined, ExportOutlined, Form, CheckCircleFilled, Checkbox, Modal } from 'gokwik-ui-kit'
import { useRef, useState, useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { navigateToUrl } from 'single-spa'
import CPV_Video from '@library/video/cpv.mp4'

const CpvSubmission = ({
    VBCLink,
    allRequiredSectionsPresent,
    reSubmitState,
    setReSubmitState,
    submitKYCDocsToEaseBuzz,
}) => {
    const merchantConfigs = useSelector(getMerchantConfigs)
    const merchantPlatform = useSelector(getMerchantPlatform)
    const userData = useSelector(getUserData)
    const [form] = Form.useForm()
    const copyTimerRef = useRef(null)
    const [showRefreshMessage, setShowRefreshMessage] = useState(false)
    const merchant_details = useSelector(getMerchantDetails)
    const dispatch = useAppDispatch()
    const [interval, initiateInterval, removeInterval] = useInterval(
        () => fetchLatestOnboardingStatusWithPollingHandling(),
        3000,
    )

    const [open, setOpen] = useState(false)

    const videoRef = useRef<HTMLVideoElement>(null)

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.playbackRate = 2.0
        }
    }, [])

    const intervalHandling = useCallback(
        (existingCpvSubmission, isCPVMandatory) => {
            if (!isCPVMandatory && existingCpvSubmission) {
                window.location.reload()
                removeInterval()
                dispatch(fetchUserDetails())
            } else {
                let timeout
                if (!timeout) {
                    window.setTimeout(() => {
                        removeInterval()
                        setShowRefreshMessage(true)
                    }, 300000)
                }
                if (!interval) {
                    initiateInterval()
                }
            }
        },
        [merchant_details?.onboarding_status?.kwik_checkout_app],
    )

    const fetchLatestOnboardingStatusWithPollingHandling = async () => {
        await makeAPICall({
            url: process.env.REACT_APP_BASE_URL + APIEndPoints.userDetails,
            method: 'get',
            skipLoader: true,
        })
            .then((result) => {
                intervalHandling(
                    result?.data?.data?.kyc_details?.existing_cpv_submission,
                    result?.data?.data?.kyc_details?.is_cpv_mandatory,
                )
                // setIsCpvMandatory(result?.data?.data?.kyc_details?.is_cpv_mandatory)
            })
            .catch((err) => {
                console.warn(err)
            })
    }
    const openInNewTab = () => {
        logEvent(
            'background_verification_new_tab_clicked',
            'click',
            'kyc',
            userData?.userDetails?.email,
            userData?.merchantDetails?.m_id,
        )
        const url = VBCLink
        if (url) {
            window.open(url, '_blank').focus()
            fetchLatestOnboardingStatusWithPollingHandling()
        }
    }

    const getCpvStatus = () => {
        let cpvStatus = ''
        if (userData?.kycData?.kyc_status === 'kyc_verification_pending' && userData?.kycData?.is_cpv_mandatory) {
            cpvStatus = 'new'
        } else if (
            userData?.kycData?.kyc_status === 'kyc_verification_failed' &&
            userData?.kycData?.existing_cpv_submission &&
            userData?.kycData?.is_cpv_mandatory
        ) {
            cpvStatus = 'rejected'
        } else if (
            userData?.kycData?.kyc_status === 'kyc_verification_failed' &&
            userData?.kycData?.is_cpv_mandatory &&
            userData?.kycData?.kyc_failure_reasons?.hasOwnProperty('cpv_error')
        ) {
            cpvStatus = 'ongoing'
        }

        return cpvStatus
    }

    const handleExitToDashboard = () => {
        let settingsPath = ''
        if (!!merchantConfigs?.supported_checkout_configs?.[MerchantConfigKeys.checkoutOne]) {
            settingsPath = '/checkout/settings'
        } else if (!!merchantConfigs?.supported_checkout_configs?.[MerchantConfigKeys.kwikCheckout]) {
            settingsPath = '/shopify-app/settings'
        } else if (!!merchantConfigs?.supported_checkout_configs?.[MerchantConfigKeys.codLite]) {
            settingsPath = '/shopify-app/settings'
        } else if (merchantPlatform === 'custom' || merchantConfigs?.is_gkp_enabled) {
            settingsPath = '/checkout-platform/settings'
        }

        return settingsPath
    }

    return (
        <div className='flex flex-col'>
            <h2 className='text-2xl font-semibold mb-2'>What’s next?</h2>
            <p className='text-gray-600 mb-6'>
                Please complete this additional step (optional) to ensure your chances of getting KYC approval quickly.
            </p>

            <div className='bg-white border border-solid border-[#D0D5DA] rounded-lg p-4'>
                <div className='flex justify-between items-center mb-2'>
                    <div className='flex items-center gap-2 mb-2'>
                        <span className='font-semibold text-base'>Contact Point Verification</span>
                    </div>
                    {/* <div className='text-blue-500 cursor-pointer' onClick={() => setOpen(true)}>
                        View Sample Video
                    </div> */}
                </div>

                <div className='bg-blue-50 text-[#004B8D] text-sm p-4 rounded-md leading-relaxed space-y-2 border border-solid border-[#B6DEFF]'>
                    <p>
                        Completing Contact Point Verification (CPV) is mandatory to validate your business authenticity
                        and ensure rapid KYC approval.
                    </p>
                </div>
            </div>

            <div className='mt-6 flex gap-4 justify-end items-end'>
                <Button
                    className='border-gray-300 text-black hover:border-gray-400'
                    onClick={() => navigateToUrl(handleExitToDashboard())}
                >
                    Exit to Dashboard
                </Button>
                <Button
                    target='_blank'
                    className={`ml-2 ${
                        userData?.kycData?.existing_cpv_submission &&
                        !userData?.kycData?.is_cpv_mandatory &&
                        '!border !border-solid !border-green-600'
                    }`}
                    variant='primary'
                    onClick={() => setOpen(true)}
                    disabled={
                        !VBCLink ||
                        (userData?.kycData?.existing_cpv_submission && !userData?.kycData?.is_cpv_mandatory) ||
                        !allRequiredSectionsPresent
                    }
                >
                    {userData?.kycData?.existing_cpv_submission && !userData?.kycData?.is_cpv_mandatory ? (
                        <>
                            <div className='text-green-600'>
                                CPV Done <CheckCircleFilled className='text-green-600' />
                            </div>
                        </>
                    ) : getCpvStatus() === 'rejected' ? (
                        <>
                            Re-Submit CPV <ExportOutlined />
                        </>
                    ) : (
                        <>
                            Verify Now <ExportOutlined />
                        </>
                    )}{' '}
                </Button>
                <Modal
                    title={'Sample CPV Video'}
                    open={open}
                    onCancel={() => setOpen(false)}
                    footer={
                        <div className='flex gap-1 justify-end items-center'>
                            <Button onClick={() => setOpen(false)}>Close</Button>

                            <Button variant='primary' onClick={openInNewTab}>
                                Proceed to CPV
                            </Button>
                        </div>
                    }
                >
                    <div className='w-full flex justify-center items-center'>
                        <video
                            ref={videoRef}
                            src={CPV_Video}
                            controls
                            style={{ maxWidth: 500 }}
                            preload='metadata'
                            autoPlay
                            loop
                        >
                            Your browser does not support the video tag.
                        </video>
                    </div>
                </Modal>
            </div>

            {userData?.kycData?.kyc_failure_reasons &&
                userData?.merchantDetails?.onboarding_status?.kwik_checkout_app === 'kyc_verification_failed' && (
                    <>
                        <br />
                        <div>
                            <div
                                className={`text-start p-4 backdrop-blur-sm bg-white/30 shadow-lg flex justify-between items-center rounded-lg z-10`}
                            >
                                <Checkbox onChange={() => setReSubmitState(!reSubmitState)} value={reSubmitState}>
                                    I have corrected all KYC errors & ready to re-submit KYC
                                </Checkbox>
                                <Button
                                    disabled={
                                        merchant_details?.onboarding_status?.kwik_checkout_app ===
                                            'kyc_verification_failed' &&
                                        userData?.kycData?.kyc_rejection_reason?.length > 0
                                            ? false
                                            : !reSubmitState
                                    }
                                    onClick={() => submitKYCDocsToEaseBuzz(true)}
                                    variant='primary'
                                >
                                    {merchant_details?.onboarding_status?.kwik_checkout_app ===
                                    'kyc_verification_failed'
                                        ? 'Re-Submit KYC'
                                        : 'Final KYC Submit'}
                                </Button>
                            </div>
                        </div>
                        <br />
                    </>
                )}
        </div>
    )
}

export default CpvSubmission
